import { Button, Tooltip } from "@heroui/react";
import { cn } from "@/utils";

type LimeFieldActionsProps = {
  children: React.ReactNode;
  actions: React.ReactNode;
  className?: string;
};

/**
 * Lays a field out beside its icon actions. The actions deliberately sit
 * outside the field rather than in its `endContent`: nested inside a select
 * trigger they are unreachable by keyboard and overflow the trigger's box.
 *
 * The action row is pinned to the height of a `size="sm"` field so the buttons
 * stay centred on the input even when an error message grows the field below.
 */
export function LimeFieldActions({
  children,
  actions,
  className,
}: LimeFieldActionsProps) {
  return (
    <div className={cn("flex w-full items-start gap-2", className)}>
      <div className="min-w-0 flex-1">{children}</div>
      <div className="flex h-12 shrink-0 items-center gap-1">{actions}</div>
    </div>
  );
}

type LimeFieldActionButtonProps = {
  label: string;
  icon: React.ReactNode;
  onPress: () => void;
  isSelected?: boolean;
  color?: "default" | "danger";
  dataIdentifier?: string;
};

export function LimeFieldActionButton({
  label,
  icon,
  onPress,
  isSelected = false,
  color = "default",
  dataIdentifier,
}: LimeFieldActionButtonProps) {
  return (
    <Tooltip content={label} closeDelay={0}>
      <Button
        isIconOnly
        size="lg"
        radius="md"
        variant={isSelected ? "flat" : "bordered"}
        color={isSelected ? color : "default"}
        aria-label={label}
        aria-pressed={isSelected}
        onPress={onPress}
        data-identifier={dataIdentifier}
      >
        {icon}
      </Button>
    </Tooltip>
  );
}

type LimeFieldActionIndicatorProps = {
  label: string;
  icon: React.ReactNode;
  className?: string;
  dataIdentifier?: string;
};

export function LimeFieldActionIndicator({
  label,
  icon,
  className,
  dataIdentifier,
}: LimeFieldActionIndicatorProps) {
  return (
    <Tooltip content={label} closeDelay={0}>
      <span
        role="img"
        aria-label={label}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-medium",
          className,
        )}
        data-identifier={dataIdentifier}
      >
        {icon}
      </span>
    </Tooltip>
  );
}
